/* eslint-disable react/display-name */
import React from 'react'
import { useTranslation } from 'react-i18next'
import DataTable from '../DataTable'
import { Box, MenuItem, Select } from '@mui/material'
import CustomLoader from '../Loader/CustomLoader'
import SearchBar from '../TableHeader/SearchBar'
import DateRangeFilter from '../TableHeader/DateRangeFilter'
import TableHeader from '../TableHeader'
import { customStyles } from '../../utils/tableCustomStyles'

const TABLE_SCROLL_HEIGHT = 'calc(100vh - 320px)'

// '' keeps the default newest-first order
const SORT_OPTIONS = [
  { value: '', label: 'SortNewest' },
  { value: 'ITEMS_DESC', label: 'SortMostItems' },
  { value: 'ITEMS_ASC', label: 'SortFewestItems' }
]

const SuperAdminOrdersData = props => {
  const { t } = useTranslation()
  const {
    orders = [],
    totalCount = 0,
    page = 1,
    limit = 10,
    onPageChange,
    onLimitChange,
    searchQuery = '',
    onSearchChange,
    startDate = '',
    endDate = '',
    onDateRangeChange,
    sortBy = '',
    onSortChange
  } = props
  const renderValue = value =>
    value === null || value === undefined ? '' : String(value)
  const renderCreatedAt = value =>
    value ? new Date(value).toLocaleString() : ''
  const renderItemsCount = items => `${items?.length || 0} ${t('Items')}`
  const renderVoucher = coupon => {
    if (!coupon?.title) return '—'
    if (!coupon.discount) return coupon.title
    const amount =
      coupon.discountType === 'PERCENTAGE'
        ? `${coupon.discount}%`
        : `£${coupon.discount}`
    return `${coupon.title} · ${amount}`
  }

  const handlePerRowsChange = perPage => {
    if (typeof onLimitChange === 'function') onLimitChange(perPage)
    if (typeof onPageChange === 'function') onPageChange(1)
  }

  const onChangeSearch = event => {
    if (typeof onSearchChange === 'function') onSearchChange(event.target.value)
  }
  const handlePageChange = nextPage => {
    if (typeof onPageChange === 'function') onPageChange(nextPage)
  }
  const tableStyles = {
    ...customStyles,
    headCells: {
      style: {
        ...customStyles.headCells.style,
        paddingLeft: '8px',
        paddingRight: '8px'
      }
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px'
      }
    },
    pagination: {
      style: {
        ...customStyles.pagination.style,
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: '#fff'
      }
    },
    tableWrapper: {
      style: {
        minWidth: '1400px'
      }
    }
  }

  const columns = [
    {
      name: t('OrderID'),
      selector: row => row.orderId,
      sortable: true,
      cell: row => <>{renderValue(row.orderId)}</>
    },
    {
      name: t('Customer'),
      selector: row => row.user?.name || '',
      sortable: true,
      cell: row => <>{renderValue(row.user?.name) || '—'}</>
    },
    {
      name: t('Phone'),
      selector: row => row.user?.phone || '',
      cell: row => <>{renderValue(row.user?.phone) || '—'}</>
    },
    {
      name: t('Items'),
      // Ranked server side via the sort picker, across every page
      selector: row => row.items?.length || 0,
      cell: row => <>{renderItemsCount(row.items)}</>
    },
    {
      name: t('Payment'),
      selector: row => row.paymentMethod,
      sortable: true,
      cell: row => <>{renderValue(row.paymentMethod)}</>
    },
    {
      name: t('Voucher'),
      selector: row => row.coupon?.title || '',
      sortable: true,
      cell: row => <>{renderVoucher(row.coupon)}</>
    },
    {
      name: 'Order Status',
      selector: row => row.orderStatus,
      sortable: true,
      cell: row => <>{renderValue(row.orderStatus)}</>
    },
    {
      name: 'Created At',
      selector: row => row.createdAt || row.orderDate,
      sortable: true,
      cell: row => <>{renderCreatedAt(row.createdAt || row.orderDate)}</>
    },
    {
      name: t('RestaurantCol'),
      selector: row => row.restaurant?.name || '',
      sortable: true,
      cell: row => <>{renderValue(row.restaurant?.name)}</>
    },
    {
      name: 'Delivery Address',
      selector: row => row.deliveryAddress?.deliveryAddress || '',
      sortable: true,
      cell: row => <>{renderValue(row.deliveryAddress?.deliveryAddress)}</>
    }
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <DataTable
        title={<TableHeader title={t('Orders')} />}
        keyField="orderId"
        columns={columns}
        data={orders}
        responsive
        progressPending={props.loading}
        onRowClicked={props.toggleModal}
        pointerOnHover
        progressComponent={<CustomLoader />}
        subHeader
        fixedHeader
        fixedHeaderScrollHeight={TABLE_SCROLL_HEIGHT}
        subHeaderComponent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: 2,
              width: '100%',
              justifyContent: 'space-between'

              // display: 'flex',
              // alignItems: 'center',
              // flexWrap: 'nowrap',
              // gap: 1.5,
              // // SearchBar lifts itself onto the title row with a -40 margin, so
              // // the range filter takes the same offset to sit inline with it.
              // '& > .orders-date-range': { marginTop: '0px' }
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DateRangeFilter
                className="orders-date-range"
                startDate={startDate}
                endDate={endDate}
                onChange={onDateRangeChange}
              />
              <Select
                size="small"
                displayEmpty
                value={sortBy}
                onChange={event =>
                  typeof onSortChange === 'function' &&
                  onSortChange(event.target.value)
                }
                sx={{
                  minWidth: 170,
                  fontSize: 13,
                  bgcolor: 'common.white',
                  borderRadius: '12px'
                }}>
                {SORT_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <SearchBar
              value={searchQuery}
              onChange={onChangeSearch}
              placeholder={t('SearchOrderVoucherOrCustomer')}
            />
          </Box>
        }
        pagination
        paginationServer
        paginationDefaultPage={page}
        paginationPerPage={limit}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationTotalRows={totalCount}
        onChangePage={handlePageChange}
        customStyles={tableStyles}
        paginationIconLastPage=""
        paginationIconFirstPage=""
      />
    </Box>
  )
}

export default SuperAdminOrdersData
